import { CartMethodOptions } from '@eliadmin/core/src/components/cart/detail/cart-method-options';
import { CartShipmentsStatus } from '@eliadmin/core/src/components/cart/detail/cart-shipments-status';
import { useCartSnapshot } from '@eliadmin/core/src/hooks/cart/use-cart-snapshot';
import { hasStoredMethodSelection } from '@eliadmin/core/src/utils/cart-method-selection';
import { cartShipmentSelectionMode } from '@eliadmin/core/src/api/cart/cart-api';
import { CartGiftSection } from '@eliadmin/core/src/components/cart/detail/cart-gift-section';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { CartSubitemEnum } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import {
  type CartItemGifts,
  type CartItemType,
  type CartPayment as CartPaymentType,
} from '@eliadmin/core/src/api/cart/cart-types';
import {
  setCartPayment,
  setCartShipment,
  type FetchCartResponse,
} from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { CartSummaryCard } from '@eliadmin/core/src/components/cart/cart-summary-card';
import { CartFreeShipment } from '@eliadmin/core/src/components/cart/detail/cart-free-shipment';
import { CartItem } from '@eliadmin/core/src/components/cart/detail/cart-item';
import { type CartShipment as CartShipmentType } from '@eliadmin/core/src/api/cart/cart-types';
import { CartPayment } from '@eliadmin/core/src/components/cart/detail/cart-payment';
import { CartShipment } from '@eliadmin/core/src/components/cart/detail/cart-shipment';
import { ProformAddrPicker } from '@eliadmin/core/src/components/cart/detail/proform-addr-picker';
import { PageHeader } from '@eliadmin/core/src/components/homepage/page-header';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { RadioGroup } from '@eliadmin/core/src/components/ui/radio-group';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { CART_CONFIG } from '@eliadmin/core/src/constants/cart-config';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartActions } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import { useCartPayments } from '@eliadmin/core/src/hooks/cart/use-cart-payments';
import { useCartShipments } from '@eliadmin/core/src/hooks/cart/use-cart-shipments';
import { useCartShipmentDestination } from '@eliadmin/core/src/hooks/cart/use-cart-shipment-destination';
import { useDimensions } from '@eliadmin/core/src/hooks/use-dimensions';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';
import type { StringOrNumber } from '@eliadmin/core/src/types/misc-types';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { isNullish } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiCreditCard, FiTruck } from 'react-icons/fi';
import { ClearCartButton } from './clear-cart-button';

export function CartDetails() {
  const { fullResponseLifecycle } = useCartSnapshot();
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const { data: cart } = useCart();
  const { data: shipments } = useCartShipments();

  const form = useFormContext<CreateOrderFormValues>();
  const destination = useCartShipmentDestination();

  const shipmentTitleRef = useRef<HTMLDivElement>(null);
  const shipmentTitleDimensions = useDimensions(shipmentTitleRef);

  const { updateCartMutation, setShipmentMutation , getUpdateCartBody } = useCartActions();
  const authToken = useCustomerStore(state => state.me?.authToken);
  const queryClient = useQueryClient();
  const { queryKey: cartQueryKey } = useCartQueryOpts();
  const [optimisticPaymentId, setOptimisticPaymentId] = useState<string | undefined>(
    cart.paymentType?.id?.toString(),
  );

  useEffect(() => {
    setOptimisticPaymentId(cart.paymentType?.id?.toString());
  }, [cart.paymentType?.id]);

  const setPaymentTypeMutation = useMutation({
    meta: { affectsCart: true },
    mutationFn: async (paymentId: string) => {
        queryClient.getQueryData<FetchCartResponse['data']>(cartQueryKey) ?? cart;

      const paymentResponse = await setCartPayment({
        locale,
        paymentId,
        authToken,
      });
      const shipmentId = paymentResponse.data.shipment?.id;

      if (shipmentId && destination.countryId != null) {
        const selectedShipment = shipments.find(
          shipment => String(shipment.id) === String(shipmentId),
        );
        const shipmentResponse = await setCartShipment({
          countryId: destination.countryId,
          locale,
          shipmentId,
          authToken,
          body: selectedShipment?.price_type === 'distance' ? destination.body : undefined,
        });

        return shipmentResponse;
      }

      return paymentResponse;
    },
    mutationKey: [CacheKey.CART_PAYMENTS, authToken],
    ...fullResponseLifecycle,
    onMutate: async (paymentId: string) => {
      const context = await fullResponseLifecycle.onMutate();
      if (context.current()) setOptimisticPaymentId(paymentId);
      return context;
    },
    onSuccess: async (response, variables, context) => {
      await fullResponseLifecycle.onSuccess(response, variables, context);
      if (context?.current()) setOptimisticPaymentId(response.data.paymentType?.id?.toString());
    },
    onError: (error, variables, context) => {
      fullResponseLifecycle.onError(error, variables, context);
      if (context?.current()) setOptimisticPaymentId(context.previousCart?.paymentType?.id?.toString());
    },
  });

  const handlePaymentChange = (value: string) => {
    const countryId = destination.countryId;
    if (isNullish(countryId)) {
      console.error('Country is not selected');
      return;
    }
    const activeShipment = shipments.find(
      shipment => String(shipment.id) === String(cart.shipment?.id),
    );
    if (activeShipment?.price_type === 'distance' && !destination.isComplete) {
      void form.trigger(destination.fieldNames);
      return;
    }

    setPaymentTypeMutation.mutate(value);
  };

  const handleShipmentChange = (value: string) => {
    const countryId = destination.countryId;
    const selectedShipment = shipments.find(shipment => String(shipment.id) === String(value));
    if (isNullish(countryId)) {
      console.error('Country is not selected');
      return;
    }
    if (selectedShipment?.price_type === 'distance' && !destination.isComplete) {
      void form.trigger(destination.fieldNames);
      return;
    }

    setShipmentMutation.mutate({
      countryId,
      locale,
      shipmentId: value,
      body: selectedShipment?.price_type === 'distance' ? destination.body : undefined,
    });
  };

  const getGifts = () => {
    const gifts: CartItemGifts[] = [];
    cart.items?.forEach(item => {
      item.sub_items.gifts?.forEach(gift => {
        if (gifts.find(g => g.id === gift.id)) {
          return;
        }
        gifts.push(gift);
      });
    });
    return gifts;
  };



  return (
    <><>
      <PageHeader
        heading={t('text343')}
        className={{
          container: 'flex-row-reverse',
          wrapper: 'pt-4',
        }}
      >
        <div className="flex min-w-max items-center">
          <Button
            className="hidden min-w-max gap-1 md:flex"
            onClick={() => (window.location.href = '/')}
            variant="outline"
          >
            {CART_CONFIG.pageHeader.cart.icon}
            {t('text208')}
          </Button>
          <Link href="/" className="text-primary hover:underline md:hidden" prefetch={false}>
            {t('text208')}
          </Link>
        </div>
      </PageHeader>

      <Container className="py-8">
        {/* <SupplierMap /> */}
        <div className="flex flex-col gap-8">
          {/* <div className="mt-4 flex flex-col items-center gap-4 md:items-start">
            <span className="text-xl font-bold">{t('text525')}</span>
            <Separator className="w-full" />
          </div> */}

          <div className="flex flex-col gap-4">
            <div className="hidden w-full px-4 font-semibold text-muted-foreground md:flex">
              <span className="min-w-[100px] text-center">{t('text20')}</span>
              <span className="ml-3 flex w-full max-w-[500px] shrink-[2] lg:shrink" />
              <div className="grid w-full grid-cols-12 gap-1 text-center">
                <span className="col-span-6">{t('text22')}</span>
                <span className="col-span-3">{t('text483')}</span>
                <span className="col-span-3">{t('text136')}</span>
              </div>
              <span className="w-full max-w-[36px]" />
            </div>
            {cart.items?.map(item => (
              <div key={item.hash}>{<CartItem item={item} />}</div>
            ))}
            {!!cart.items?.length && (
              <div className="flex justify-end">
                <ClearCartButton />
              </div>
            )}
          </div>
<CartGiftSection />


          <div className="flex w-full flex-col justify-start gap-4 md:flex-row">
            <Suspense fallback={null}>
              <CartFreeShipment />
            </Suspense>
            {/* <CartApplyCoupon /> */}
            <ProformAddrPicker />
          </div>

          {/* {!!cart.coupons?.length && (
            <div className="flex flex-col gap-2">
              <span className="text-xl font-bold">{t('text123')}</span>
              {cart.coupons?.map((coupon, i) => (
                <CartCouponCard key={`coupon-${i}`} coupon={coupon} />
              ))}
            </div>
          )} */}

          {/* <div className="hidden flex-row justify-end md:flex">{countryField}</div> */}

          <div
            className={cn(
              'flex flex-col gap-4',
              // cart.delivery_address !== null
              //   ? String(cart.delivery_address) === '0'
              //   : cart.address === null && 'pointer-events-none opacity-50',
            )}
          >
            <span className="text-xl font-bold">{t('text641')}</span>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* shipments */}
              <Card variant="outlined" className="">
                <CardContent className="h-full px-6 py-8 md:px-8">
                  <Suspense
                    fallback={
                      <>
                        <Skeleton className="h-[58px]" />
                        <Skeleton className="h-[58px]" />
                      </>
                    }
                  >
                    <RadioGroup
                      name="shipments"
                      className="flex flex-col gap-2"
                      onValueChange={handleShipmentChange}
                    >
                      <div
                        ref={shipmentTitleRef}
                        className="spacing mb-2 flex w-full flex-col justify-between gap-2 @container sm:mb-0 sm:flex-row sm:items-center"
                      >
                        <div
                          className="flex flex-row items-center gap-4"
                          //style={{ height: shipmentTitleDimensions?.height }}
                        >
                          <FiTruck size={20} />
                          <span className="text-xl font-bold">{t('text125')}</span>
                        </div>
                        {/* <div className="w-auto">
                        <FormField
                          control={form.control}
                          name={'country.id'}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-2 @lg:max-w-[400px] @lg:flex-row @lg:items-center">
                              <FormLabel className="min-w-max text-xs text-muted-foreground">
                                {t('text602')}
                              </FormLabel>
                              <FormControl>
                                <Select value={String(field.value)} onValueChange={field.onChange}>
                                  <SelectTrigger className="@lg:min-w-[200px]">
                                    <SelectValue placeholder={t('text602')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {countries.data?.map(c => (
                                      <SelectItem key={c.id} value={String(c.id)}>
                                        {c.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div> */}
                      </div>
                      <CartShipments />
                    </RadioGroup>
                  </Suspense>
                </CardContent>
              </Card>
              {/* payments */}
              <Card variant="outlined" className="">
                <CardContent className="h-full px-6 py-8 md:px-8">
                  <RadioGroup
                    name="payments"
                    value={optimisticPaymentId ?? cart.paymentType?.id?.toString()}
                    className="flex flex-col gap-2"
                    onValueChange={handlePaymentChange}
                  >
                    <div
                      className="flex max-h-[48px] flex-row items-center gap-4 md:max-h-none"
                      style={{
                        height: shipmentTitleDimensions?.height,
                      }}
                    >
                      <FiCreditCard size={20} />
                      <span className="text-xl font-bold">{t('text124')}</span>
                    </div>
                    <Suspense fallback={<Skeleton className="h-[58px]" />}>
                      <ProformCartPayments
                        optimisticPaymentId={optimisticPaymentId}
                        onPaymentChange={paymentId => setPaymentTypeMutation.mutate(paymentId)}
                      />
                    </Suspense>
                  </RadioGroup>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Button
              onClick={() => (window.location.href = '/')}
              variant="outline"
              className="hidden w-max md:flex"
            >
              {t('text480')}
            </Button>
            <Suspense fallback={<Skeleton className="h-64" />}>
              <CartSummaryCard itemsDisplayType="total" showContinueButton />
            </Suspense>
          </div>
        </div>
      </Container>

    </></>
  );
}

function ProformCartPayments({
  onPaymentChange,
  optimisticPaymentId,
}: {
  onPaymentChange: (paymentId: string) => void;
  optimisticPaymentId?: string;
}) {
  const { isUpdating: methodsUpdating, data: payments } = useCartPayments();
  const { data: cart } = useCart();
  const form = useFormContext<CreateOrderFormValues>();

  useEffect(
    function refreshActivePaymentOnMount() {
      if (methodsUpdating || !payments?.length) return;
      if (hasStoredMethodSelection(payments ?? [])) return;
      const activePaymentId = cart.paymentType?.id;
      const payment = !isNullish(activePaymentId)
        ? payments?.find(
            (payment: CartPaymentType) => String(payment.id) === String(activePaymentId),
          )
        : undefined;
      const countryId = form.watch('different_delivery_address')
        ? form.watch('country2.id')
        : form.watch('country.id');

      if (!payment && !!payments?.length && !isNullish(payments[0]?.id) && !isNullish(countryId)) {
        onPaymentChange(String(payments[0].id));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methodsUpdating, payments],
  );

  return (
    <CartMethodOptions disabled={methodsUpdating}>
      <>
        {payments?.map((payment: CartPaymentType) => (
          <CartPayment key={payment.id} payment={payment} optimisticPaymentId={optimisticPaymentId} />
        ))}
      </>
    </CartMethodOptions>
  );
}

export function CartShipments() {
  const shipmentState = useCartShipments();
  const { data: shipments } = shipmentState;
  const { data: cart } = useCart();
  const form = useFormContext<CreateOrderFormValues>();
  const destination = useCartShipmentDestination();
  const countryId = destination.countryId;
  const region = (form.watch as (name: string) => unknown)('region') as StringOrNumber | undefined;
  const { setShipmentMutation } = useCartActions();
  const locale = useLocaleStore(s => s.currentLocale);

  const [previousShipments, setPreviousShipments] = useState<CartShipmentType[]>([]);

  useEffect(() => {
    if (shipments?.length) {
      setPreviousShipments(shipments);
    }
  }, [shipments]);

  const [shipmentSet, setShipmentSet] = useState(false);
  const [countryIdChanged, setCountryIdChanged] = useState(false);
  const previousCountryId = useRef<typeof countryId>(countryId);

  useEffect(() => {
    if (previousCountryId.current !== countryId) {
      previousCountryId.current = countryId;
      setCountryIdChanged(true);
    }
  }, [countryId]);

  useEffect(
    function setShipmentOnMount() {
      if (shipmentState.isUpdating) return;
      if (!shipmentState.isSuccess || shipmentState.isUpdating) return;
      if (cartShipmentSelectionMode !== 'explicit' && hasStoredMethodSelection(shipments)) return;
      if (
        (!shipmentSet || countryIdChanged) &&
        (isNullish(cart.shipment?.id) || countryIdChanged) &&
        !!shipments.length &&
        !isNullish(shipments[0]?.id) &&
        !isNullish(countryId) &&
        shipments[0].price_type !== 'distance'
      ) {
        setShipmentSet(true);
        setCountryIdChanged(false);
        setShipmentMutation.mutate({
          countryId: countryId,
          regionId: region,
          locale,
          shipmentId: shipments[0].id,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shipmentState.isUpdating, shipmentState.isSuccess, shipmentState.isUpdating, shipments, countryId],
  );

  // Choose to render current shipments or fallback to previousShipments if shipments array is empty
  const shipmentsToRender = shipments;

  if (!shipmentState.hasCountry) return <CartShipmentsStatus status="country" />;
  if (shipmentState.isError) return <CartShipmentsStatus status="error" busy={shipmentState.isUpdating} onRetry={() => { void shipmentState.refetch(); }} />;
  if (shipmentState.isPending) return <CartShipmentsStatus status="loading" busy />;
  if (!shipments.length) return <CartShipmentsStatus status="empty" />;

  return (
    <CartMethodOptions disabled={shipmentState.isUpdating}>
      <>
        {shipmentsToRender.map((shipment: CartShipmentType) => (
          <CartShipment key={shipment.id} shipment={shipment} isLoading={!(shipments.length > 0)} />
        ))}
      </>
    </CartMethodOptions>
  );
}
